import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';
import {
  FUTURES_CARD_NARROW_WIDTH,
  FUTURES_CARD_TIGHT_WIDTH,
  getFuturesCardScorerKitSize,
} from './FuturesCard.constants';

export const getFuturesCardStyles = (theme: Theme, windowWidth: number, themeMode: ThemeMode = 'light') => {
  const narrow = windowWidth < FUTURES_CARD_NARROW_WIDTH;
  const tight = windowWidth < FUTURES_CARD_TIGHT_WIDTH;
  const isDark = themeMode === 'dark';

  const cardMarginH = tight ? theme.spacing.xs : narrow ? theme.spacing.sm : theme.spacing.lg;
  const picksGap = tight ? 6 : narrow ? 8 : 10;
  const pickPadH = tight ? 5 : narrow ? 6 : 8;
  const flagW = tight ? 30 : narrow ? 34 : 38;
  const flagH = Math.round(flagW * (50 / 72));
  const pickKitScale = tight ? 0.78 : narrow ? 0.86 : 0.94;
  const mainFlagW = tight ? 78 : narrow ? 90 : 102;
  const mainFlagH = Math.round(mainFlagW * 0.66);
  const scorerKitDisplaySize = getFuturesCardScorerKitSize(tight, narrow);
  const mediaBlockBuffer = tight ? 3 : narrow ? 4 : 5;
  const mediaBlockH = Math.max(mainFlagH, scorerKitDisplaySize) + mediaBlockBuffer;

  /** Fixed-width tiles, centered — not 50% / full-bleed columns */
  const innerRowBudget = Math.max(0, windowWidth - 2 * cardMarginH);
  const maxTileFromScreen = Math.floor((innerRowBudget - picksGap) / 2);
  const minTileW = tight ? 106 : narrow ? 112 : 118;
  const idealTileW = tight ? 128 : narrow ? 140 : 152;
  const rawTileW = Math.min(idealTileW, maxTileFromScreen);
  const withMinW = Math.max(minTileW, rawTileW);
  const pickTileWidth = withMinW <= maxTileFromScreen ? withMinW : rawTileW;

  return StyleSheet.create({
    card: {
      marginHorizontal: cardMarginH,
      marginBottom: theme.spacing.xs,
      paddingTop: theme.spacing.xs,
      paddingBottom: theme.spacing.xs,
      maxWidth: '100%',
      alignSelf: 'stretch',
      ...(Platform.OS === 'web' && {
        maxWidth: 560,
        width: '100%',
        alignSelf: 'center',
      }),
    },

    picksRow: {
      alignSelf: 'center',
      flexDirection: 'row',
      alignItems: 'stretch',
      justifyContent: 'center',
      gap: picksGap,
      marginBottom: 0,
      maxWidth: '100%',
    },

    picksRowBottomSpacer: {
      marginBottom: 8,
    },

    lockedCaption: {
      marginTop: 6,
      marginBottom: 8,
      paddingHorizontal: tight ? theme.spacing.xs : theme.spacing.sm,
      alignSelf: 'center',
      maxWidth: '100%',
    },
    lockedCaptionText: {
      fontSize: tight ? 11 : 12,
      fontWeight: '600',
      color: theme.gray[600],
      textAlign: 'center',
      lineHeight: tight ? 15 : 16,
    },
    pickCardShell: {
      width: pickTileWidth,
      flexShrink: 0,
      alignSelf: 'stretch',
      shadowColor: theme.shadow,
      shadowOffset: { width: 0, height: isDark ? 3 : 6 },
      shadowOpacity: isDark ? 0.35 : 0.12,
      shadowRadius: isDark ? 10 : 12,
      elevation: isDark ? 5 : 8,
      ...(Platform.OS === 'web' && {
        boxShadow: isDark
          ? ('0 6px 20px rgba(0,0,0,0.45)' as never)
          : ('0 6px 18px rgba(107, 70, 193, 0.12)' as never),
      }),
    },

    pickCard: {
      backgroundColor: theme.background.card,
      borderRadius: tight ? 18 : 20,
      borderWidth: 1,
      borderColor: isDark ? theme.gray[700] : theme.gray[200],
      width: '100%',
      alignItems: 'stretch',
      overflow: 'hidden',
    },
    pickCardPressed: {
      opacity: 0.92,
    },
    pickCardHover: {
      borderColor: isDark ? theme.primaryLight + '99' : theme.primary + '55',
    },
    pickCardSettled: {
      opacity: 0.78,
    },

    pickCardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tight ? 4 : 5,
      paddingVertical: tight ? 5 : 7,
      paddingHorizontal: pickPadH,
      backgroundColor: theme.primary,
    },
    pickCardHeaderLabel: {
      fontSize: tight ? 9 : 10,
      fontWeight: '800',
      color: theme.onPrimary,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      textAlign: 'center',
      flexShrink: 1,
    },
    pickCardBody: {
      position: 'relative',
      backgroundColor: theme.background.card,
      paddingTop: tight ? 5 : 7,
      paddingBottom: tight ? 6 : 8,
      paddingHorizontal: tight ? 8 : 10,
      alignItems: 'center',
      justifyContent: 'flex-start',
      overflow: 'visible',
    },
    pickCardMedia: {
      width: '100%',
      maxWidth: '100%',
      minHeight: mediaBlockH,
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'relative',
      overflow: 'visible',
      marginTop: tight ? 2 : 3,
    },
    pickKitScaleOuter: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    pickKitScaleInner: {
      transform: [{ scale: pickKitScale }],
    },
    pickCardFooter: {
      backgroundColor: theme.background.card,
      paddingTop: tight ? 2 : 3,
      paddingBottom: tight ? 8 : 10,
      paddingHorizontal: tight ? 8 : 10,
      alignItems: 'center',
      borderTopWidth: 0,
    },
    pickCardNameWrap: {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      alignItems: 'center',
    },
    pickCardName: {
      fontSize: narrow ? 12 : 13,
      fontWeight: '900',
      color: theme.gray[900],
      textAlign: 'center',
      lineHeight: narrow ? 16 : 17,
      width: '100%',
      maxWidth: '100%',
    },
    pickCardPlaceholder: {
      color: theme.gray[600],
      fontWeight: '600',
    },
    flagImage: {
      width: flagW,
      height: flagH,
      borderRadius: tight ? 5 : 6,
      maxWidth: '100%',
    },
    flagPlaceholder: {
      width: flagW,
      height: flagH,
      borderRadius: tight ? 5 : 6,
      maxWidth: '100%',
      backgroundColor: theme.gray[100],
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.gray[300],
    },

    mainFlagPlate: {
      width: mainFlagW,
      height: mainFlagH,
      borderRadius: tight ? 10 : 12,
      overflow: 'hidden',
      backgroundColor: isDark ? theme.gray[800] : theme.gray[100],
      borderWidth: 1,
      borderColor: isDark ? theme.gray[600] : theme.gray[200],
      ...(Platform.OS === 'web' && {
        boxShadow: isDark
          ? ('0 2px 6px rgba(0,0,0,0.4)' as never)
          : ('0 2px 6px rgba(0,0,0,0.08)' as never),
      }),
    },
    mainFlagPlateImage: {
      width: '100%',
      height: '100%',
    },
    mainFlagPlateEmpty: {
      flex: 1,
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isDark ? theme.gray[800] : theme.gray[100],
    },
    scorerKitOverlay: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3,
    },

    lockBadge: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: tight ? 20 : 22,
      height: tight ? 20 : 22,
      borderRadius: tight ? 10 : 11,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: isDark ? 'rgba(0,0,0,0.72)' : 'rgba(15,10,26,0.72)',
      borderWidth: 1.5,
      borderColor: isDark ? theme.gray[400] : theme.gray[500],
    },

    settledBadge: {
      position: 'absolute',
      top: 4,
      right: 4,
      width: 22,
      height: 22,
      borderRadius: 11,
      justifyContent: 'center',
      alignItems: 'center',
    },
    settledCorrect: {
      backgroundColor: theme.success + '40',
    },
    settledWrong: {
      backgroundColor: theme.error + '40',
    },
    settledBadgeText: {
      fontSize: 10,
    },
  });
};
